import {
  ChatAlt2Icon,
  CodeIcon,
  CreditCardIcon,
  CursorClickIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PauseIcon,
  ReceiptRefundIcon,
  SearchIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import { OrderLookup, ProductCards, ShopifySetup } from '../mocks/ShopifyMocks';
import SiteChatDemo from '../mocks/SiteChatDemo';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Juniper Candle Co.',
  initial: 'J',
  bg: 'bg-[#9a3412]',
};

/*
 * Facts this page relies on (checked 2026-10-21, docs/agents/shopify.mdx and
 * the Shopify card in /api/bots/integrations): paid plans; a private app
 * made in the owner's Shopify Dev Dashboard with read_products and
 * read_orders (read_all_orders for orders older than 60 days), nothing from
 * the App Store; product search with prices and links and order status
 * after the order number and checkout email match, in the website chat,
 * the shareable link and the Playground only; pausing stops the store data,
 * the chat stays on the theme until its code is removed.
 */
export function useShopifyContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'shopify',
    name: 'Shopify',
    logo: 'FaShopify',
    seoTitle: t(
      'Shopify AI Chatbot: Product Search and Order Status in Your Store',
    ),
    seoDescription: t(
      'An AI chatbot for your Shopify store. It finds products with prices and links, shares order status safely and answers shipping and returns questions, 24/7.',
    ),
    hero: {
      label: t('Shopify AI chatbot'),
      title: t('A shop assistant that knows your catalog.'),
      accent: t('And where every order is.'),
      lead: t(
        'Connect your Shopify store and paste one line into your theme. Your AI agent finds products with prices and links, answers shipping and returns questions, and shares order status once the customer’s details match.',
      ),
      facts: [
        t('Product search and order status'),
        t('Your own app, not from the App Store'),
        t('Shopify on paid plans'),
      ],
      description: t(
        'Example: a shopper on a candle store asks for a candle that smells like the sea. The agent searches the Shopify catalog and shows two matching candles with their prices.',
      ),
      demo: (
        <SiteChatDemo
          brand={BRAND}
          url='junipercandles.myshopify.com'
          site={{
            headline: t('Hand-poured candles for slow evenings.'),
            picture:
              'bg-[linear-gradient(135deg,#fdba74_0%,#9a3412_55%,#431407_100%)]',
            accent: 'text-[#9a3412] dark:text-[#fdba74]',
          }}
          lines={[
            {
              from: 'visitor',
              text: t('Do you have a candle that smells like the sea?'),
              at: 700,
            },
            {
              from: 'agent',
              content: t('Yes! These two are customer favorites:'),
              typingAt: 1200,
              at: 2000,
            },
            {
              from: 'agent',
              content: (
                <ProductCards
                  products={[
                    {
                      name: 'Sea Salt & Sage',
                      price: '$28',
                      swatch: 'bg-[linear-gradient(140deg,#7dd3fc,#0369a1)]',
                    },
                    {
                      name: 'Driftwood',
                      price: '$32',
                      swatch: 'bg-[linear-gradient(140deg,#e7d7bd,#8b7354)]',
                    },
                  ]}
                />
              ),
              at: 2600,
            },
          ]}
        />
      ),
      wide: true,
      steps: [],
    },
    jobs: {
      title: t('Every question between browsing and buying.'),
      tail: t('Answered on the spot.'),
      lead: t(
        'Shoppers leave when they can’t find an answer. Your agent knows your catalog and your policies, so it answers straight away and points them to the right product.',
      ),
      items: [
        {
          icon: SearchIcon,
          title: t('Finds products'),
          detail: t(
            'Searches your catalog and suggests matching products with prices and links.',
          ),
        },
        {
          icon: TruckIcon,
          title: t('Shares order status'),
          detail: t(
            'Tells customers where their order is once the order number and checkout email match.',
          ),
        },
        {
          icon: ReceiptRefundIcon,
          title: t('Shipping and returns'),
          detail: t(
            'Explains delivery times, costs and your returns policy, from your own pages.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Shows buttons'),
          detail: t(
            'Sends visitors to booking, pricing or checkout pages with a button in the chat.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
      ],
    },
    setup: {
      title: t('Connect your store in three steps.'),
      tail: t('No app to install from the App Store.'),
      steps: [
        {
          title: t('Create an app in Shopify'),
          detail: t(
            'In your Shopify admin, open Settings, then Apps and sales channels, and build an app in the Dev Dashboard with read_products and read_orders.',
          ),
        },
        {
          title: t('Connect your store'),
          detail: t(
            'In your agent’s Shopify window, enter your .myshopify.com domain and the app’s Client ID and Client secret, and click Save.',
          ),
        },
        {
          title: t('Add the chat to your theme'),
          detail: t(
            'Copy the chat widget code and paste it into layout/theme.liquid, just before </body>.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/shopify',
      guideLabel: t('Read the Shopify setup guide'),
      description: t(
        'The agent’s Shopify window with the candle store connected, read access to products and orders, and the chat code ready to paste into the theme.',
      ),
      visual: <ShopifySetup shop='junipercandles.myshopify.com' />,
    },
    spotlight: {
      title: t('Order status, without the risk.'),
      tail: t('Details first, then the answer.'),
      lead: t(
        'When a customer asks where their order is, the agent asks for the order number and the email used at checkout, and shares the status only when both match.',
      ),
      points: [
        t('The order number and checkout email must match'),
        t('Orders older than 60 days with read_all_orders'),
        t('In the store chat and on your shareable link'),
        t('Revoke access by uninstalling the app'),
      ],
      description: t(
        'The store chat asking a customer for her order number and checkout email, then sharing that the order is out for delivery, with a note that it was shared only after both matched.',
      ),
      visual: (
        <OrderLookup
          brand={BRAND}
          order='#1093'
          status={t('Out for delivery')}
          lines={[
            { from: 'visitor', text: t('Where’s my order?') },
            {
              from: 'agent',
              text: t(
                'Happy to check! What’s your order number and the email you used at checkout?',
              ),
            },
            { from: 'visitor', text: '#1093, maya@example.com' },
            {
              from: 'agent',
              text: t(
                'Thanks, Maya! Order #1093 is out for delivery and should arrive today.',
              ),
            },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Shopify, in plain words.'),
      items: [
        {
          icon: CreditCardIcon,
          title: t('Paid plans'),
          detail: t(
            'Shopify is on paid plans, and you can build and test your agent for free first.',
          ),
        },
        {
          icon: ShieldCheckIcon,
          title: t('Read-only access'),
          detail: t(
            'The app you create can only read products and orders. Uninstall it in Shopify to revoke access at once.',
          ),
        },
        {
          icon: ChatAlt2Icon,
          title: t('In your store’s chat'),
          detail: t(
            'Product search and order status work in the chat on your store, the shareable link and the Playground, not on WhatsApp or other channels.',
          ),
        },
        {
          icon: CodeIcon,
          title: t('New theme, same code'),
          detail: t(
            'If you publish another theme later, paste the chat code into that theme too.',
          ),
        },
        {
          icon: PauseIcon,
          title: t('Pause at any time'),
          detail: t(
            'Switch the integration off and the agent stops using your store data. The chat stays on your theme until you remove the code.',
          ),
        },
        {
          icon: ShoppingBagIcon,
          title: t('More for online stores'),
          detail: t(
            'See how an AI agent helps e-commerce teams, from sizing questions to returns, on any platform.',
          ),
          link: {
            href: '/chatbots/ecommerce',
            label: t('AI agents for e-commerce'),
          },
        },
      ],
    },
    faqs: [
      {
        question: t('Is it a Shopify app?'),
        answer: t(
          'Not one from the App Store. You create a private app in your own Shopify account with read access to products and orders, so nothing is installed from the App Store and you can revoke access at any time.',
        ),
      },
      {
        question: t('Can it tell customers where their order is?'),
        answer: t(
          'Yes, once the customer gives the order number and the email used at checkout. It shares the status only when both match.',
        ),
      },
      {
        question: t('Can it find orders older than 60 days?'),
        answer: t(
          'Yes, if you add the read_all_orders permission to the app. Without it, the agent can look up orders from the last 60 days.',
        ),
      },
      {
        question: t('Does product search work on WhatsApp or Messenger?'),
        answer: t(
          'Product search and order status work in the chat on your store, the shareable link and the Playground. On other channels the agent answers from your knowledge.',
        ),
      },
      {
        question: t('Which plan do I need?'),
        answer: t(
          'Shopify is on paid plans. You can build and test your agent on the Free plan first.',
        ),
      },
      {
        question: t('Will it slow down my site?'),
        answer: t(
          'No. The bubble shows as soon as the page loads, and the chat itself loads in the background once the page has settled.',
        ),
      },
      {
        question: tc('What can my agent learn from?'),
        answer: tc(
          'Websites and sitemaps, PDF, Word, CSV, TXT and EPUB files, YouTube videos, plain text and Q&A pairs. You can retrain it with one click whenever something changes.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your store could be answering shoppers by lunchtime.'),
    finalExchange: {
      question: t('Do your candles come in a gift box?'),
      answer: t('Yes! Add gift wrapping at checkout for $4.'),
    },
  };
}
